// src/components/HousingNavigatorDashboard.js — test-suite dashboard shown
// under the Housing Navigator demo chat. Data is a snapshot of a suite run
// against production (src/data/housingnavigatorResults.json), produced by
// the suite runner: conversations → turns, each with an answer key, the
// bot's answer, a verdict, grader reasons and (for listing searches) a
// verification against Housing Navigator's own search API.
import React, { useMemo, useState } from 'react';
import data from '../data/housingnavigatorResults.json';

// HNMI Brand Style Guide 2023 (Blue, Dark Blue, Gray 20, Gray 70) and the
// HNMI Data Style Guide 2024 for the chart colours: its "good" green, "bad"
// red and secondary orange for the bars, the palette's lightest steps for chip
// backgrounds. Chip text is darkened from the same hue to keep 4.5:1.
const BLUE = '#0B66AD';
const DARK = '#1F3466';
const MUTED = '#54595D';
const LINE = '#C8CCD1';
const TINT = '#E8F0F7';
const SERIF = "'Faustina', Georgia, 'Times New Roman', serif";

const VERDICT = {
  pass: { label: 'Pass', bg: '#EEF6EC', fg: '#2E6B22', bar: '#57A73F' },
  partial: { label: 'Partial', bg: '#FBF0E4', fg: '#8A4A0E', bar: '#DD842B' },
  fail: { label: 'Fail', bg: '#FBE8EB', fg: '#A10F27', bar: '#D91636' },
};
const KIND = {
  help: { label: 'Help Center', fg: BLUE, bg: TINT },
  context: { label: 'Conversation', fg: '#8B3181', bg: '#F5ECF4' },
  listing: { label: 'Listing search', fg: '#0F6157', bg: '#E8F5F3' },
  trick: { label: 'Trick / off-limits', fg: '#9A3420', bg: '#F8ECE9' },
  widen: { label: 'Widened search', fg: DARK, bg: '#D1E1EF' },
  lang: { label: 'Languages (11)', fg: '#5E3F7A', bg: '#EEE9F3' },
};

const Badge = ({ verdict }) => {
  const v = VERDICT[verdict] || VERDICT.partial;
  return <span className="inline-block text-xs font-semibold rounded-full px-2 py-0.5 whitespace-nowrap" style={{ background: v.bg, color: v.fg }}>{v.label}</span>;
};
const KindChip = ({ kind }) => {
  const k = KIND[kind] || KIND.help;
  return <span className="inline-block text-xs rounded px-1.5 py-0.5 whitespace-nowrap" style={{ background: k.bg, color: k.fg }}>{k.label}</span>;
};

function Tile({ label, value, color, sub }) {
  return (
    <div className="rounded-xl bg-white border px-4 py-3 text-center" style={{ borderColor: LINE, minWidth: 92 }}>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs" style={{ color: MUTED }}>{label}</div>
      {sub && <div className="text-[10px]" style={{ color: MUTED }}>{sub}</div>}
    </div>
  );
}

const ScoreBar = ({ c }) => {
  const n = Math.max(1, c.pass + c.partial + c.fail);
  return (
    <div className="flex h-2 w-full rounded overflow-hidden" style={{ background: TINT }}>
      <div style={{ width: `${(c.pass / n) * 100}%`, background: VERDICT.pass.bar }} />
      <div style={{ width: `${(c.partial / n) * 100}%`, background: VERDICT.partial.bar }} />
      <div style={{ width: `${(c.fail / n) * 100}%`, background: VERDICT.fail.bar }} />
    </div>
  );
};

// The chat returns HTML limited to <a>, <br> and listing <img>; strip everything else.
function safeHtml(htmlStr) {
  return String(htmlStr || '')
    .replace(/<(?!\/?(a|br|img)\b)[^>]*>/gi, '')
    .replace(/<img\b([^>]*)>/gi, (m, attrs) => {
      const src = (attrs.match(/src="(https:\/\/search\.housingnavigatorma\.org[^"]+)"/i) || [])[1];
      const alt = (attrs.match(/alt="([^"]*)"/i) || [])[1] || '';
      return src ? `<img src="${src}" alt="${alt.replace(/"/g, '')}" loading="lazy" style="display:block;max-width:300px;height:auto;border-radius:8px;margin:6px 0">` : '';
    })
    .replace(/<a\b([^>]*)>/gi, (m, attrs) => {
      const href = (attrs.match(/href="([^"]+)"/i) || [])[1] || '#';
      return `<a href="${href.replace(/"/g, '')}" target="_blank" rel="noopener noreferrer" style="color:${BLUE};text-decoration:underline">`;
    })
    .replace(/\n/g, '<br>');
}

const countVerdicts = turns => turns.reduce((c, t) => ({ ...c, [t.verdict]: (c[t.verdict] || 0) + 1 }), { pass: 0, partial: 0, fail: 0 });

function Turn({ t, n }) {
  const [showKey, setShowKey] = useState(true);
  const ver = t.verification;
  return (
    <div className="px-4 py-3" style={{ borderTop: `1px solid ${LINE}` }}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm" style={{ color: DARK }}>
          <span className="font-semibold">Turn {n}</span> <span className="text-xs" style={{ color: MUTED }}>{t.secs}s</span>
          <br /><span className="font-medium">You:</span> “{t.q}”
        </p>
        <Badge verdict={t.verdict} />
      </div>
      <div className="mt-2 rounded-lg p-3 text-sm leading-relaxed" style={{ background: '#F2FAFF', color: '#000' }} dangerouslySetInnerHTML={{ __html: safeHtml(t.html) }} />
      <div className="mt-2 grid gap-2 md:grid-cols-2 text-xs">
        <div className="rounded-lg p-2" style={{ background: '#fff', border: `1px solid ${LINE}` }}>
          <button type="button" onClick={() => setShowKey(s => !s)} className="font-semibold" style={{ color: DARK }}>Answer key {showKey ? '−' : '+'}</button>
          {showKey && (
            <div className="mt-1" style={{ color: MUTED }}>
              <div>{t.expect}</div>
              <div className="mt-1 italic">Source: {t.source}</div>
            </div>
          )}
        </div>
        <div className="rounded-lg p-2" style={{ background: (VERDICT[t.verdict] || VERDICT.partial).bg, border: `1px solid ${(VERDICT[t.verdict] || VERDICT.partial).bar}` }}>
          <div className="font-semibold" style={{ color: (VERDICT[t.verdict] || VERDICT.partial).fg }}>Grader</div>
          {t.reasons && t.reasons.length > 0 ? (
            <ul className="mt-1 list-disc pl-4" style={{ color: '#000' }}>{t.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
          ) : <div className="mt-1" style={{ color: '#000' }}>Every key matched.</div>}
          {ver && (
            <div className="mt-1" style={{ color: ver.ok === false ? VERDICT.fail.fg : VERDICT.pass.fg }}>
              API check: {ver.ok === null ? 'not run' : ver.ok ? 'verified against Housing Navigator’s search API' : 'mismatch'}{ver.apiTotal != null ? ` (API total ${ver.apiTotal})` : ''}
            </div>
          )}
        </div>
      </div>
      {t.links && t.links.length > 0 && (
        <div className="mt-2 text-xs" style={{ color: MUTED }}>
          Links: {t.links.slice(0, 6).map((l, i) => <a key={i} href={l} target="_blank" rel="noopener noreferrer" className="underline mr-2" style={{ color: BLUE }}>{l.replace(/^https?:\/\//, '').slice(0, 48)}</a>)}
        </div>
      )}
    </div>
  );
}

function ConversationCard({ conv, verdictFilter }) {
  const [open, setOpen] = useState(false);
  const c = countVerdicts(conv.turns);
  const shown = verdictFilter === 'ALL' ? conv.turns : conv.turns.filter(t => t.verdict === verdictFilter);
  return (
    <div className="rounded-xl bg-white border" style={{ borderColor: LINE }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-mono" style={{ color: MUTED, minWidth: 34 }}>{conv.id}</span>
        <span className="flex-1 text-sm font-medium min-w-[200px]" style={{ color: DARK }}>
          {conv.subject}
          <span className="block text-xs font-normal" style={{ color: MUTED }}>{conv.section} · {conv.turns.length} turn{conv.turns.length > 1 ? 's' : ''} · “{conv.turns[0].q.slice(0, 70)}{conv.turns[0].q.length > 70 ? '…' : ''}”</span>
        </span>
        <KindChip kind={conv.kind} />
        <span className="flex gap-1 text-xs">
          {c.fail > 0 && <span style={{ color: VERDICT.fail.fg }}>{c.fail} fail</span>}
          {c.partial > 0 && <span style={{ color: VERDICT.partial.fg }}>{c.partial} partial</span>}
          <span style={{ color: VERDICT.pass.fg }}>{c.pass} pass</span>
        </span>
        <span className="text-xs" style={{ color: MUTED }}>{open ? '−' : '+'}</span>
      </button>
      {open && shown.map(t => <Turn key={t.i} t={t} n={t.i + 1} />)}
    </div>
  );
}

const HousingNavigatorDashboard = () => {
  const [verdictFilter, setVerdictFilter] = useState('ALL');
  const [kindFilter, setKindFilter] = useState('ALL');
  const [section, setSection] = useState('ALL');
  const [query, setQuery] = useState('');

  const convs = data.conversations || [];
  const allTurns = useMemo(() => convs.flatMap(c => c.turns), [convs]);
  const totals = useMemo(() => countVerdicts(allTurns), [allTurns]);
  const n = allTurns.length || 1;
  const score = Math.round(((totals.pass + 0.5 * totals.partial) / n) * 1000) / 10;
  const strict = Math.round((totals.pass / n) * 1000) / 10;
  const sections = useMemo(() => ['ALL', ...Array.from(new Set(convs.map(c => c.section)))], [convs]);
  const byKind = useMemo(() => Object.keys(KIND).map(k => ({ k, c: countVerdicts(convs.filter(x => x.kind === k).flatMap(x => x.turns)) })), [convs]);

  const visible = useMemo(() => convs.filter(c =>
    (kindFilter === 'ALL' || c.kind === kindFilter) &&
    (section === 'ALL' || c.section === section) &&
    (verdictFilter === 'ALL' || c.turns.some(t => t.verdict === verdictFilter)) &&
    (!query || (c.subject + ' ' + c.turns.map(t => t.q + ' ' + t.a).join(' ')).toLowerCase().includes(query.toLowerCase()))
  ), [convs, kindFilter, section, verdictFilter, query]);

  const chip = (active, onClick, label) => (
    <button type="button" onClick={onClick} aria-pressed={active} className="rounded-full px-3 py-1 border text-sm"
      style={active ? { background: BLUE, color: '#fff', borderColor: BLUE } : { background: '#fff', color: DARK, borderColor: LINE }}>{label}</button>
  );

  return (
    <section className="w-full max-w-5xl mx-auto mt-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
        <div>
          <h2 className="text-2xl" style={{ color: DARK, fontFamily: SERIF, fontWeight: 600 }}>Test suite results</h2>
          <p className="text-sm" style={{ color: MUTED }}>
            {convs.length} conversations, {allTurns.length} turns, run against this bot on {data.ranAtLabel || new Date((data.ranAt || 0) * 1000).toLocaleDateString()}.
            Every turn is graded against an answer key taken from Housing Navigator&apos;s Resources page and the PDFs linked from it; listing searches are checked against Housing Navigator&apos;s own search API.
            Widened searches check that a search with no match in the town widens (nearby towns, fewer filters, or more availability statuses) instead of saying there is nothing.
            The language section asks the same six questions in all 11 widget languages; an AI grader (gpt-4o-mini) checks each answer is in the language asked and matches the answer key. Where the grader misread an answer, it was reviewed by hand and the note is shown on that turn.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Tile label="Score" value={`${score}%`} color={DARK} sub={`${strict}% strict`} />
          <Tile label="Pass" value={totals.pass} color={VERDICT.pass.fg} />
          <Tile label="Partial" value={totals.partial} color={VERDICT.partial.fg} />
          <Tile label="Fail" value={totals.fail} color={VERDICT.fail.fg} />
        </div>
      </div>
      <ScoreBar c={totals} />

      <div className="grid gap-2 md:grid-cols-3 mt-4">
        {byKind.map(({ k, c }) => (
          <button type="button" key={k} onClick={() => setKindFilter(kindFilter === k ? 'ALL' : k)} aria-pressed={kindFilter === k} className="rounded-xl bg-white border p-3 text-left"
            style={{ borderColor: kindFilter === k ? BLUE : LINE }}>
            <div className="flex items-center justify-between"><KindChip kind={k} /><span className="text-xs" style={{ color: MUTED }}>{c.pass + c.partial + c.fail} turns</span></div>
            <div className="mt-2"><ScoreBar c={c} /></div>
            <div className="mt-1 text-xs" style={{ color: MUTED }}>{c.pass} pass · {c.partial} partial · {c.fail} fail</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4 mb-4">
        {chip(verdictFilter === 'ALL', () => setVerdictFilter('ALL'), 'All')}
        {chip(verdictFilter === 'pass', () => setVerdictFilter('pass'), `Pass (${totals.pass})`)}
        {chip(verdictFilter === 'partial', () => setVerdictFilter('partial'), `Partial (${totals.partial})`)}
        {chip(verdictFilter === 'fail', () => setVerdictFilter('fail'), `Fail (${totals.fail})`)}
        <select aria-label="Section" value={section} onChange={e => setSection(e.target.value)} className="rounded-full px-3 py-1 border text-sm bg-white" style={{ borderColor: LINE, color: DARK }}>
          {sections.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All sections' : s}</option>)}
        </select>
        <input type="search" aria-label="Search questions and answers" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search questions and answers" className="rounded-full px-3 py-1 border text-sm flex-1 min-w-[200px]" style={{ borderColor: LINE, color: DARK }} />
        <span className="text-xs" style={{ color: MUTED }}>{visible.length} of {convs.length} conversations</span>
      </div>

      <div className="grid gap-2">
        {visible.map(conv => <ConversationCard key={conv.id} conv={conv} verdictFilter={verdictFilter} />)}
        {visible.length === 0 && <p className="text-sm" style={{ color: MUTED }}>Nothing matches those filters.</p>}
      </div>

      <p className="text-xs mt-6" style={{ color: MUTED }}>
        Pass: every key phrase present, nothing forbidden, links present. Partial: correct but a link or a soft key is missing. Fail: a key fact missing, a forbidden claim, or a listing that is not in the API results for those filters. Score counts a partial as half.
      </p>
    </section>
  );
};

export default HousingNavigatorDashboard;
